// PHA-1168: which custom views this clinic has, and which one this tab is showing.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { customViewsApi, CustomViewKind, CustomViewSummary, CustomViewSurface } from '../../services/api';

export interface CustomViewsState {
  enabled: boolean;
  canDesign: boolean;
  canShare: boolean;
  views: CustomViewSummary[];
  loading: boolean;
  refresh: () => void;
}

/**
 * Feature state + saved views for (org, surface). Re-fetched whenever the org
 * changes. "ALL" (the internal cross-clinic view) and an unresolved org never
 * have custom views -- views belong to a clinic, and render refuses ALL.
 * Reports (kind 'report') are the exception: Phanova staff design and run them
 * across all clinics, so ALL is a real org there.
 */
export function useCustomViews(orgId: string, surface: CustomViewSurface, kind: CustomViewKind = 'view'): CustomViewsState {
  const isReport = kind === 'report';
  const [state, setState] = useState<Omit<CustomViewsState, 'refresh'>>({
    enabled: false, canDesign: false, canShare: false, views: [], loading: false,
  });
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!orgId || (orgId === 'ALL' && !isReport)) {
      setState({ enabled: false, canDesign: false, canShare: false, views: [], loading: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const flag = isReport ? await customViewsApi.enabled(orgId, 'report') : await customViewsApi.enabled(orgId);
        if (!flag.enabled) {
          if (!cancelled) setState({ enabled: false, canDesign: false, canShare: false, views: [], loading: false });
          return;
        }
        const list = isReport ? await customViewsApi.list(orgId, surface, 'report') : await customViewsApi.list(orgId, surface);
        if (!cancelled) {
          setState({
            enabled: true, canDesign: list.can_design, canShare: list.can_share,
            views: list.views, loading: false,
          });
        }
      } catch (err) {
        // Fail closed to Phanova Standard: a custom-views outage must never take
        // the Patient List or Billing Report down with it.
        console.warn('Custom views unavailable:', err);
        if (!cancelled) setState({ enabled: false, canDesign: false, canShare: false, views: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, surface, tick, isReport]);

  return { ...state, refresh };
}

/** Just the feature flag + design right, for nav items (no view list fetch). */
export function useCustomViewsAccess(
  orgId: string,
  kind: CustomViewKind = 'view',
): { enabled: boolean; canDesign: boolean; loading: boolean } {
  const isReport = kind === 'report';
  const [state, setState] = useState({ enabled: false, canDesign: false, loading: true });
  useEffect(() => {
    if (!orgId || (orgId === 'ALL' && !isReport)) {
      setState({ enabled: false, canDesign: false, loading: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (isReport ? customViewsApi.enabled(orgId, 'report') : customViewsApi.enabled(orgId))
      .then((r) => { if (!cancelled) setState({ enabled: r.enabled, canDesign: r.enabled && r.can_design, loading: false }); })
      .catch(() => { if (!cancelled) setState({ enabled: false, canDesign: false, loading: false }); });
    return () => { cancelled = true; };
  }, [orgId, isReport]);
  return state;
}

export const STANDARD = 'standard' as const;
export type ViewChoice = number | typeof STANDARD | null; // null = "the clinic default"

const choiceKey = (surface: CustomViewSurface, orgId: string) => `customViews.choice:${surface}:${orgId}`;

const readChoice = (surface: CustomViewSurface, orgId: string): ViewChoice => {
  try {
    const raw = sessionStorage.getItem(choiceKey(surface, orgId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return parsed === STANDARD || typeof parsed === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * The view this TAB shows for (org, surface): an explicit pick (kept per org in
 * sessionStorage, like the tab's org itself -- PHA-1123), else the nearest
 * clinic default, else Phanova Standard. A pick that no longer exists (archived,
 * or from another clinic) falls back to the default.
 */
export function useViewChoice(orgId: string, surface: CustomViewSurface, views: CustomViewSummary[], enabled: boolean) {
  const [choice, setChoiceState] = useState<ViewChoice>(() => (orgId ? readChoice(surface, orgId) : null));

  useEffect(() => {
    setChoiceState(orgId ? readChoice(surface, orgId) : null);
  }, [orgId, surface]);

  const setChoice = useCallback((next: ViewChoice) => {
    setChoiceState(next);
    try {
      if (next === null) sessionStorage.removeItem(choiceKey(surface, orgId));
      else sessionStorage.setItem(choiceKey(surface, orgId), JSON.stringify(next));
    } catch {
      /* storage unavailable: the choice lasts for this render tree only */
    }
  }, [orgId, surface]);

  const activeViewId = useMemo<number | null>(() => {
    if (!enabled) return null;
    if (choice === STANDARD) return null;
    if (typeof choice === 'number' && views.some((v) => v.id === choice)) return choice;
    const def = views.find((v) => v.effective_default);
    return def ? def.id : null;
  }, [enabled, choice, views]);

  return { choice, setChoice, activeViewId };
}
